class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems =
        this.closest('cart-items') || this.closest('cart-drawer-items') || this.closest('main-cart-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));

    // Quantity button handlers (no debounce for immediate server update)
    this.quantityButtonDebounceTimers = new Map();
    this.addEventListener('click', this.handleQuantityButtonClick.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });

    // Initialize accessibility states for all quantity inputs
    this.initializeQuantityAccessibility();
  }

  initializeQuantityAccessibility() {
    const quantityInputs = this.querySelectorAll('input[id^="Quantity-"]');
    quantityInputs.forEach((input) => {
      const line = input.dataset.index;
      const quantity = parseInt(input.value);
      if (line && !isNaN(quantity)) {
        this.updateQuantityButtonStates(line, quantity);
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    // Only validate quantity inputs, not other inputs like gift message textarea
    if (!event.target.dataset.index || !event.target.dataset.quantityVariantId) {
      return;
    }

    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement?.getAttribute('name') || '',
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  handleQuantityButtonClick(event) {
    const button = event.target.closest('.js-quantity-decrease, .js-quantity-increase');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const line = button.dataset.line;
    const isDecrease = button.classList.contains('js-quantity-decrease');

    // Check if buttons are disabled (loading state)
    if (button.disabled) return;

    // Show loading immediately if a change will occur
    const quantityInputImmediate = this.querySelector(`#Quantity-${line}`);
    if (quantityInputImmediate) {
      const currentValueImmediate = parseInt(quantityInputImmediate.value);
      const newValueImmediate = isDecrease
        ? Math.max(0, currentValueImmediate - 1)
        : Math.min(99, currentValueImmediate + 1);
      if (newValueImmediate !== currentValueImmediate) {
        this.enableLoading(line);
      }
    }

    // Immediately compute and submit the intended new value
    const quantityInput = this.querySelector(`#Quantity-${line}`);
    if (!quantityInput) return;

    const currentValue = parseInt(quantityInput.value);
    const newValue = isDecrease ? Math.max(0, currentValue - 1) : Math.min(99, currentValue + 1);

    if (newValue === currentValue) {
      return; // no-op at bounds
    }

    // Call server update immediately; UI will refresh on response
    const variantId = quantityInput.dataset.quantityVariantId;
    this.updateQuantity(line, newValue, event, '', variantId);
  }

  updateQuantityDisplay(line, quantity) {
    const displayElement = this.querySelector(`#QuantityText-${line} .quantity-display`);
    const pluralElement = this.querySelector(`#QuantityText-${line} [data-single][data-multiple]`);

    if (displayElement) {
      displayElement.textContent = quantity;
      displayElement.setAttribute('aria-label', `Current quantity: ${quantity}`);
    }

    if (pluralElement) {
      pluralElement.textContent =
        quantity > 1 ? pluralElement.getAttribute('data-multiple') : pluralElement.getAttribute('data-single');
    }
  }

  updateQuantityButtonStates(line, quantity) {
    const decreaseButton = this.querySelector(`.js-quantity-decrease[data-line="${line}"]`);
    const increaseButton = this.querySelector(`.js-quantity-increase[data-line="${line}"]`);

    if (decreaseButton) {
      const isDisabled = quantity <= 0;
      decreaseButton.setAttribute('aria-disabled', isDisabled.toString());
    }

    if (increaseButton) {
      const isDisabled = quantity >= 99;
      increaseButton.setAttribute('aria-disabled', isDisabled.toString());
    }
  }

  storeFocusState(line) {
    const activeElement = document.activeElement;
    if (!activeElement) return null;

    // Check if the focused element is a quantity button for this line
    if (activeElement.classList.contains('js-quantity-decrease') && activeElement.dataset.line === line) {
      return { type: 'decrease-button', line: line };
    }
    if (activeElement.classList.contains('js-quantity-increase') && activeElement.dataset.line === line) {
      return { type: 'increase-button', line: line };
    }

    // Check if the focused element is the quantity input for this line
    if (activeElement.id === `Quantity-${line}`) {
      return { type: 'quantity-input', line: line };
    }

    return null;
  }

  restoreFocusState(focusInfo, parsedState) {
    if (!focusInfo) return;

    // If the item was removed (quantity = 0), don't try to restore focus
    const itemStillExists = parsedState.items.some((item, index) => index + 1 === parseInt(focusInfo.line));
    if (!itemStillExists) return;

    // Use setTimeout to ensure DOM is fully updated
    setTimeout(() => {
      let elementToFocus = null;

      switch (focusInfo.type) {
        case 'decrease-button':
          elementToFocus = this.querySelector(`.js-quantity-decrease[data-line="${focusInfo.line}"]`);
          break;
        case 'increase-button':
          elementToFocus = this.querySelector(`.js-quantity-increase[data-line="${focusInfo.line}"]`);
          break;
        case 'quantity-input':
          elementToFocus = document.getElementById(`Quantity-${focusInfo.line}`);
          break;
      }

      if (elementToFocus && typeof elementToFocus.focus === 'function') {
        elementToFocus.focus();
      }
    }, 50); // Small delay to ensure DOM is fully rendered
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      // Store focus information before full DOM replacement
      const focusInfo = this.storeFocusForDrawerUpdate();

      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }

          // Restore focus after DOM replacement
          this.restoreFocusAfterDrawerUpdate(focusInfo);
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  storeFocusForDrawerUpdate() {
    const activeElement = document.activeElement;
    if (!activeElement) return null;

    // Check if focused element is within the cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || !cartDrawer.contains(activeElement)) return null;

    // Check if the focused element is a quantity button
    if (activeElement.classList.contains('js-quantity-decrease')) {
      return { type: 'decrease-button', line: activeElement.dataset.line };
    }
    if (activeElement.classList.contains('js-quantity-increase')) {
      return { type: 'increase-button', line: activeElement.dataset.line };
    }

    // Check if the focused element is a quantity input
    const quantityMatch = activeElement.id?.match(/^Quantity-(\d+)$/);
    if (quantityMatch) {
      return { type: 'quantity-input', line: quantityMatch[1] };
    }

    return null;
  }

  restoreFocusAfterDrawerUpdate(focusInfo) {
    if (!focusInfo) return;

    // Use setTimeout to ensure new DOM is fully rendered
    setTimeout(() => {
      let elementToFocus = null;

      switch (focusInfo.type) {
        case 'decrease-button':
          elementToFocus = document.querySelector(`.js-quantity-decrease[data-line="${focusInfo.line}"]`);
          break;
        case 'increase-button':
          elementToFocus = document.querySelector(`.js-quantity-increase[data-line="${focusInfo.line}"]`);
          break;
        case 'quantity-input':
          elementToFocus = document.getElementById(`Quantity-${focusInfo.line}`);
          break;
      }

      if (elementToFocus && typeof elementToFocus.focus === 'function') {
        elementToFocus.focus();

        // Also reinitialize accessibility states for the new elements
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems && typeof cartDrawerItems.initializeQuantityAccessibility === 'function') {
          cartDrawerItems.initializeQuantityAccessibility();
        }
      }
    }, 100); // Slightly longer delay for full DOM replacement
  }

  getSectionsToRender() {
    return [
      {
        id: 'header-basket',
        section: 'header-basket',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    // Store focus information before DOM update
    const focusInfo = this.storeFocusState(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        // Update sections
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartItemsElement = document.querySelector('cart-items');

        if (cartItemsElement) cartItemsElement.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const element = document.getElementById(section.id);
          if (element && parsedState.sections[section.section]) {
            const elementToReplace = element.querySelector(section.selector) || element;
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        // Restore focus after DOM update
        this.restoreFocusState(focusInfo, parsedState);

        // Reinitialize accessibility states after DOM update
        this.initializeQuantityAccessibility();

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        // Ensure hidden input value is synced to confirmed quantity to make subsequent clicks work
        const confirmedInput = document.getElementById(`Quantity-${line}`);
        if (confirmedInput && typeof updatedValue !== 'undefined') {
          confirmedInput.value = updatedValue;
          confirmedInput.setAttribute('value', updatedValue);
        }

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });

        // Dispatch custom event for main cart shipping recalculation
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cartData: parsedState } }));
      })
      .catch(() => {
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.js_cart-item__error-text');
      if (errorText) {
        errorText.textContent = message;
        // Show or hide the error based on whether there's a message
        if (message && message.trim()) {
          lineItemError.classList.remove('hidden');
        } else {
          lineItemError.classList.add('hidden');
        }
      }
    }

    // Also display errors in the cart-level error container
    const cartErrors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (cartErrors && message && message.trim()) {
      cartErrors.textContent = message;
      cartErrors.classList.remove('hidden');
    } else if (cartErrors) {
      cartErrors.textContent = '';
      cartErrors.classList.add('hidden');
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('pointer-events-none', 'select-none');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    // Disable quantity buttons during loading
    const quantityButtons = this.querySelectorAll(`[data-line="${line}"]`);
    quantityButtons.forEach((button) => {
      button.disabled = true;
    });

    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('pointer-events-none', 'select-none');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));

    // Re-enable quantity buttons after loading
    const quantityButtons = this.querySelectorAll(`[data-line="${line}"]`);
    quantityButtons.forEach((button) => {
      button.disabled = false;
    });
  }
}

customElements.define('cart-items', CartItems);

class MainCartItems extends CartItems {
  constructor() {
    super();

    // Initialize shipping calculator
    this.initShippingCalculator();

    // Listen for cart updates from product forms and other sources
    document.addEventListener('cart:updated', (event) => {
      this.handleCartUpdate(event.detail);
    });

    // Listen for pub/sub cart updates (from cart drawer, etc.)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Don't sync if the update came from main cart itself
        if (event.source !== 'main-cart') {
          this.handleCartUpdate(event);
        }
      });
    }
  }

  // Initialize shipping cost calculator
  initShippingCalculator() {
    const initWithCalculator = () => {
      if (window.shippingCalculator) {
        this.shippingUnsubscriber = window.shippingCalculator.subscribe((shippingData) => {
          this.displayShippingCost(shippingData);
        });
        window.shippingCalculator.calculateShipping();
      }
    };

    if (window.shippingCalculator) {
      initWithCalculator();
    } else {
      let checkCount = 0;
      const checkInterval = setInterval(() => {
        checkCount++;
        if (window.shippingCalculator || checkCount > 50) {
          clearInterval(checkInterval);
          if (window.shippingCalculator) {
            initWithCalculator();
          }
        }
      }, 100);
    }
  }

  // Display shipping cost (same as CartDrawerItems)
  displayShippingCost(shippingData) {
    const amountElement = document.getElementById('shipping-cost-amount');
    const methodNameElement = document.getElementById('shipping-method-name');

    if (!amountElement) return;

    if (!shippingData || shippingData.status === 'empty') {
      amountElement.textContent = '—';
      if (methodNameElement) methodNameElement.textContent = 'Shipping';
      this.updateCartTotal(0);
      return;
    }

    if (shippingData.status === 'unavailable' || shippingData.status === 'postcode_required') {
      amountElement.textContent = 'Calculated at checkout';
      if (methodNameElement) methodNameElement.textContent = 'Shipping';
      this.lastShippingCost = null;
      this.lastShippingMethodText = 'Shipping';
      this.lastShippingAmountText = 'Calculated at checkout';
      this.updateCartTotal(null);
      return;
    }

    if (shippingData.status === 'error') {
      amountElement.textContent = 'Unable to calculate';
      if (methodNameElement) methodNameElement.textContent = 'Shipping';
      this.lastShippingCost = null;
      this.lastShippingMethodText = 'Shipping';
      this.lastShippingAmountText = 'Unable to calculate';
      this.updateCartTotal(null);
      return;
    }

    if (shippingData.status === 'available' && shippingData.cheapestRate) {
      const cheapestRate = shippingData.cheapestRate;
      const shippingCostInPence = shippingData.shippingCost || 0;

      if (methodNameElement) {
        methodNameElement.textContent = 'Shipping Estimate';
      }

      if (shippingData.isFreeShipping) {
        amountElement.textContent = 'Free';
      } else if (window.shippingCalculator) {
        const formattedPrice = window.shippingCalculator.formatCurrency(shippingCostInPence, shippingData.country);
        amountElement.textContent = formattedPrice;
      } else {
        const formattedPrice = new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP',
        }).format(shippingCostInPence / 100);
        amountElement.textContent = formattedPrice;
      }

      this.lastShippingCost = shippingCostInPence;
      this.lastShippingMethodText = methodNameElement ? methodNameElement.textContent : 'Shipping Estimate';
      this.lastShippingAmountText = amountElement ? amountElement.textContent : '—';
      this.updateCartTotal(shippingCostInPence);
    } else {
      // Fallback: if we have shipping cost data but don't match the expected structure
      if (shippingData.shippingCost !== undefined) {
        const shippingCostInPence = shippingData.shippingCost || 0;

        if (methodNameElement) {
          methodNameElement.textContent = 'Shipping Estimate';
        }

        if (shippingData.isFreeShipping) {
          amountElement.textContent = 'Free';
        } else if (window.shippingCalculator) {
          const formattedPrice = window.shippingCalculator.formatCurrency(shippingCostInPence, shippingData.country);
          amountElement.textContent = formattedPrice;
        } else {
          const formattedPrice = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
          }).format(shippingCostInPence / 100);
          amountElement.textContent = formattedPrice;
        }

        this.lastShippingCost = shippingCostInPence;
        this.lastShippingMethodText = methodNameElement ? methodNameElement.textContent : 'Shipping Estimate';
        this.lastShippingAmountText = amountElement ? amountElement.textContent : '—';
        this.updateCartTotal(shippingCostInPence);
      } else {
        amountElement.textContent = '—';
        if (methodNameElement) methodNameElement.textContent = 'Shipping Estimate';
        this.lastShippingCost = 0;
        this.lastShippingMethodText = 'Shipping Estimate';
        this.lastShippingAmountText = '—';
        this.updateCartTotal(0);
      }
    }
  }

  // Update cart total with shipping
  updateCartTotal(shippingCostInPence) {
    const totalElement = document.getElementById('cart-total-amount');
    if (!totalElement) return;

    const cartSubtotalInPence = parseInt(totalElement.dataset.subtotal);

    if (shippingCostInPence === null) {
      const formattedSubtotal = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(cartSubtotalInPence / 100);
      totalElement.textContent = formattedSubtotal;
      this.lastTotalText = formattedSubtotal;
    } else {
      const totalInPence = cartSubtotalInPence + shippingCostInPence;
      const formattedTotal = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(totalInPence / 100);
      totalElement.textContent = formattedTotal;
      this.lastTotalText = formattedTotal;
    }
  }

  // Handle cart updates from external sources (like cart drawer)
  async handleCartUpdate(eventDetail) {
    // If this update came from the main cart itself, just update shipping
    if (eventDetail.source === 'main-cart') {
      if (window.shippingCalculator) {
        window.shippingCalculator.clearCache();
        window.shippingCalculator.calculateShipping(eventDetail.cartData || eventDetail);
      }
      return;
    }

    // Update came from external source (cart drawer, product forms, etc.)
    // Fetch fresh content for the main cart
    try {
      const sectionId = this.dataset.sectionId;
      const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
      const sectionHTML = await response.text();

      if (response.ok) {
        const html = new DOMParser().parseFromString(sectionHTML, 'text/html');
        const cartData = eventDetail.cartData || eventDetail;

        if (cartData && cartData.item_count === 0) {
          // Cart is now empty, replace main-cart-items with empty cart content
          const newSection = html.querySelector('.shopify-section');
          const newContainer = newSection.querySelector('.js-main-cart-container');
          if (newContainer) {
            const h2Element = newContainer.querySelector('h2');
            if (h2Element && h2Element.nextElementSibling) {
              this.outerHTML = h2Element.nextElementSibling.outerHTML;
              return;
            }
          }
        } else {
          // Cart has items, update the content
          const newContent = html.querySelector('main-cart-items .main-cart-content');
          const currentContent = this.querySelector('.main-cart-content');
          if (newContent && currentContent) {
            currentContent.innerHTML = newContent.innerHTML;

            // Reinitialize shipping after external update
            setTimeout(() => {
              this.reinitializeShipping();
            }, 100);
          }
        }
      }
    } catch (error) {
      // Failed to sync cart content - silently handle error
    }

    // Always update shipping calculation
    if (window.shippingCalculator) {
      window.shippingCalculator.clearCache();
      window.shippingCalculator.calculateShipping(eventDetail.cartData || eventDetail);
    }
  }

  // Update header basket count manually
  updateHeaderBasketCount(itemCount) {
    const headerBasket = document.getElementById('header-basket');

    if (headerBasket) {
      // Update the visible count number (the span with aria-hidden="true" that shows the count)
      const tabularNumsSpan = headerBasket.querySelector('.js-cart-count-container');

      if (tabularNumsSpan) {
        const countSpan = tabularNumsSpan.querySelector('.js-cart-count-number');

        if (countSpan) {
          // Display the count or "99+" if over 99
          countSpan.textContent = itemCount < 100 ? itemCount : '99+';
        }

        // Update the screen reader text
        const srOnlySpan = tabularNumsSpan.querySelector('.js-cart-count-sr');

        if (srOnlySpan) {
          // This would need the translation, but for now let's use a simple format
          const itemText = itemCount === 1 ? 'item' : 'items';
          srOnlySpan.textContent = `${itemCount} ${itemText} in cart`;
        }
      }
    }
  }

  // Reinitialize shipping after content updates
  reinitializeShipping() {
    // Clean up existing subscription
    if (this.shippingUnsubscriber) {
      this.shippingUnsubscriber();
    }

    // Reinitialize shipping calculator
    this.initShippingCalculator();
  }

  // Override updateQuantity to use main cart section
  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    // Store focus information before DOM update
    const focusInfo = this.storeFocusState(line);

    const sectionId = this.dataset.sectionId;
    const body = JSON.stringify({
      line,
      quantity,
      sections: [sectionId],
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          const quantityElement = document.getElementById(`Quantity-${line}`);
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute('value');
          }
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // Update the main cart content
        if (parsedState.sections && parsedState.sections[sectionId]) {
          const html = new DOMParser().parseFromString(parsedState.sections[sectionId], 'text/html');

          if (parsedState.item_count === 0) {
            // Cart is now empty, replace main-cart-items with empty cart content
            const newSection = html.querySelector('.shopify-section');

            // Look for empty cart content (everything after the h2)
            const newContainer = newSection.querySelector('.js-main-cart-container');
            if (newContainer) {
              const h2Element = newContainer.querySelector('h2');
              if (h2Element && h2Element.nextElementSibling) {
                // Replace this main-cart-items element with the empty cart content
                this.outerHTML = h2Element.nextElementSibling.outerHTML;
                return; // Don't continue with shipping reinitialization since cart is empty
              }
            }
          } else {
            // Cart still has items, replace just the main-cart-content
            const newContent = html.querySelector('main-cart-items .main-cart-content');
            const currentContent = this.querySelector('.main-cart-content');
            if (newContent && currentContent) {
              currentContent.innerHTML = newContent.innerHTML;
            }
          }
        }

        // Restore focus after DOM update
        this.restoreFocusState(focusInfo, parsedState);

        // Reinitialize accessibility states after DOM update
        this.initializeQuantityAccessibility();

        // Update header basket count manually since section rendering fails
        this.updateHeaderBasketCount(parsedState.item_count);

        // Store the current shipping values before reinitializing
        const lastShippingCost = this.lastShippingCost || 0;
        const lastShippingMethodText = this.lastShippingMethodText || 'Shipping Estimate';
        const lastShippingAmountText = this.lastShippingAmountText || '—';
        const lastTotalText = this.lastTotalText;

        // Immediately restore shipping display to prevent flash
        const methodElement = document.getElementById('shipping-method-name');
        const amountElement = document.getElementById('shipping-cost-amount');
        const totalElement = document.getElementById('cart-total-amount');

        if (methodElement && lastShippingMethodText) {
          methodElement.textContent = lastShippingMethodText;
        }
        if (amountElement && lastShippingAmountText) {
          amountElement.textContent = lastShippingAmountText;
        }
        if (totalElement && lastTotalText) {
          totalElement.textContent = lastTotalText;
        }

        // Reinitialize shipping after content update
        setTimeout(() => {
          this.reinitializeShipping();

          // If we had a shipping cost, reapply it immediately to avoid the flash of incorrect total
          if (lastShippingCost > 0) {
            setTimeout(() => {
              this.updateCartTotal(lastShippingCost);
            }, 50);
          }
        }, 100);

        // Dispatch cart updated event
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              ...parsedState,
              source: 'main-cart',
              cartData: parsedState,
            },
          })
        );

        // Publish to pub/sub system for other cart components
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'main-cart',
            cartData: parsedState,
            variantId: variantId,
          });
        }

        this.updateLiveRegions(line, '');
      })
      .catch(() => {
        const errors = document.getElementById('cart-errors');
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.shippingUnsubscriber) {
      this.shippingUnsubscriber();
    }
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
}

customElements.define('main-cart-items', MainCartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() => {
              // Update hidden input fields in cart forms
              const noteValue = event.target.value;
              const cartNoteInput = document.getElementById('cart-note-input');
              const drawerNoteInput = document.getElementById('CartDrawer-note-input');
              if (cartNoteInput) cartNoteInput.value = noteValue;
              if (drawerNoteInput) drawerNoteInput.value = noteValue;
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
